import React, { useState } from "react";
import { Modal, notification, Steps } from "antd";
import { IconContainer, MainContainer } from "./styles";
import { defaultValues, steps } from "./defaultValues";
import { CreateGoalModalProps, Form } from "./types";
import { SmartGoalRequest } from "../../types";
import { companyGoalsService } from "../../../../../../services/modules/companyGoals";
import Typography from "../../../../../../components/Typography";
import Button from "../../../../../../components/Button";
import ThemeColors from "../../../../../../themes/colors";
import useEnterprise from "../../../../hooks/useEnterprise";
import { Column, Row } from "../../../../../../themes/globalStyle";
import CustomTextArea from "../../../../../../components/TextArea";
import { FcIdea } from "react-icons/fc";
import GradeModal from "./components/GradeModal";
import CompletionModal from "./components/CompletionModal";
import { subAccountService } from "../../../../../../services/modules/subAccount";

const CreateModalModal: React.FC<CreateGoalModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  onCreate,
}): JSX.Element => {
  const { enterpriseState } = useEnterprise();

  const [form, setForm] = useState<Form>(defaultValues);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState(0);
  const [questionModal, setQuestionModal] = useState<boolean>(false);
  const [completionModal, setCompletionModal] = useState<boolean>(false);

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const mapFormToRequest = (grade: string) => {
    return {
      title: `Objetivo-${new Date()}`,
      a: form.a,
      s: form.s,
      m: form.m,
      goal: grade,
      r: form.r,
      t: form.t,
    } as SmartGoalRequest;
  };

  const validateModal = () => {
    if (
      form.s.length > 0 &&
      form.m.length > 0 &&
      form.a.length > 0 &&
      form.r.length > 0 &&
      form.t.length > 0
    ) {
      return true;
    }

    return false;
  };

  const handleCreateGoal = (grade: string) => {
    setIsLoading(true);

    const mapped = mapFormToRequest(grade);

    if (mapped && enterpriseState.selectedEnterprise) {
      companyGoalsService
        .createCompanyGoals(enterpriseState.selectedEnterprise, mapped)
        .then(() => {
          notification.success({
            message: "Sucesso",
            description: "Objetivo criado!",
          });

          setCompletionModal(true);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const StepIcon = (props: { step: string; stepKey: number }) => (
    <div
      style={{
        width: "1.5rem",
        height: "1.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "100%",
        backgroundColor:
          current >= props.stepKey
            ? ThemeColors.Purple500
            : ThemeColors.Purple200,
      }}
    >
      <Typography text={props.step} size="tiny" color="Bg300" weight={500} />
    </div>
  );

  const onChange = (value: number) => {
    setCurrent(value);
  };

  const handleComplete = () => {
    if (
      enterpriseState &&
      enterpriseState.selectedEnterprise &&
      enterpriseState.enterpriseData
    ) {
      subAccountService
        .defineSmartGoalsDone(enterpriseState.selectedEnterprise)
        .then(() => {
          notification.success({
            message: "Sucesso",
            description: "Objetivo criado!",
          });
        })
        .finally(() => onCreate());
    }
  };

  return (
    <Modal
      destroyOnClose
      title={
        <Typography
          text="Novo Objetivo"
          size="large"
          color="Black500"
          weight={600}
        />
      }
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      width={"70%"}
      centered
      footer={
        <Row style={{ justifyContent: "flex-end" }}>
          {current < 4 ? (
            <Button
              size="middle"
              borderRadius="0.5rem"
              style={{ width: "fit-content" }}
              backgroundColor={ThemeColors.Black500}
              loading={isLoading}
              onClick={() => current < 4 && setCurrent((prev) => prev + 1)}
            >
              <Typography
                text="Avançar"
                size="medium"
                color="Bg300"
                weight={400}
              />
            </Button>
          ) : (
            <>
              {validateModal() ? (
                <Button
                  size="middle"
                  borderRadius="0.5rem"
                  style={{ width: "fit-content" }}
                  backgroundColor={ThemeColors.Black500}
                  loading={isLoading}
                  onClick={() => setQuestionModal(true)}
                >
                  <Typography
                    text="Criar"
                    size="medium"
                    color="Bg300"
                    weight={400}
                  />
                </Button>
              ) : (
                <Button
                  size="middle"
                  borderRadius="0.5rem"
                  style={{ width: "fit-content" }}
                  backgroundColor={ThemeColors.Black200}
                  disabled
                >
                  <Typography
                    text="Preencha todos os dados"
                    size="medium"
                    color="Bg300"
                    weight={400}
                  />
                </Button>
              )}
            </>
          )}
        </Row>
      }
    >
      <MainContainer>
        <Steps
          direction="vertical"
          size="small"
          current={current}
          onChange={onChange}
          items={[
            {
              icon: <StepIcon step="S" stepKey={0} />,
              title: "Específico",
              description: "Qual seu objetivo detalhado?",
            },
            {
              icon: <StepIcon step="M" stepKey={1} />,
              title: "Mensurável",
              description: "Como será medido?",
            },
            {
              icon: <StepIcon step="A" stepKey={2} />,
              title: "Alcançável",
              description: "O objetivo é possível de alcançar?",
            },
            {
              icon: <StepIcon step="R" stepKey={3} />,
              title: "Relevante",
              description: "Por que é importante?",
            },
            {
              icon: <StepIcon step="T" stepKey={4} />,
              title: "Temporal",
              description: "Qual é o prazo?",
            },
          ]}
        />
        {
          <Column>
            <IconContainer>
              <FcIdea size={28} />
              <Typography
                text={steps[current].title}
                size="small"
                color="Black400"
                weight={400}
              />
            </IconContainer>
            <CustomTextArea
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  [steps[current].field]: e.target.value,
                }))
              }
              value={form[steps[current].field as keyof unknown]}
              placeholder={steps[current].placeholder}
              autoSize={{ minRows: 10, maxRows: 12 }}
            />
          </Column>
        }
      </MainContainer>
      <GradeModal
        modalOpen={questionModal}
        setQuestionModal={setQuestionModal}
        onSelectGrade={(grade) => {
          setQuestionModal(false);
          handleCreateGoal(grade);
        }}
      />
      <CompletionModal
        modalOpen={completionModal}
        onAddNew={() => onCreate()}
        onComplete={() => handleComplete()}
        setCompletionModal={setCompletionModal}
      />
    </Modal>
  );
};

export default CreateModalModal;
